import { useCallback, useState } from "react";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Divider,
  IconButton,
  List,
  ListItem,
  ListItemText,
  Stack,
  Typography,
} from "@mui/material";
import { useAuth } from "react-oidc-context";
import SyncIcon from "@mui/icons-material/Sync";
import SyncProblemIcon from "@mui/icons-material/SyncProblem";
import DownloadIcon from "@mui/icons-material/Download";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import ReplayIcon from "@mui/icons-material/Replay";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import AssignmentIndIcon from "@mui/icons-material/AssignmentInd";
import { useTranslation } from "i18n-fe";
import { useScoreContext } from "../context/ScoreContext";
import { syncEngine } from "../local/sync";
import { dumpDatabase } from "../local/db";
import { localDataExportFilename } from "../util/localDataExport";
import { classNumberLabel, formatVenueTime } from "../util/classDisplay";
import {
  describeOutboxOps,
  describeRideForDiscard,
  isClaimable,
  isHeldForDisplay,
  isNotAssignedScribeError,
} from "../local/outboxDetail";

// #2111 — same pattern as scoreadmin-fe's PublishResults.jsx
// triggerBrowserDownload: a plain Blob + hidden <a download> click, no
// server round trip. JSON here (the whole local DB dump), not CSV.
function triggerJsonDownload(filename, obj) {
  const blob = new Blob([JSON.stringify(obj, null, 2)], {
    type: "application/json",
  });
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement("a");
  anchor.href = url;
  anchor.download = filename;
  document.body.appendChild(anchor);
  anchor.click();
  document.body.removeChild(anchor);
  URL.revokeObjectURL(url);
}

// #1921 — i18n keys must be static literals (never `t(\`prefix.${var}\`)`), so
// op type -> label is a plain switch of literal t() calls rather than a
// template-literal key built off op.type.
function opTypeLabel(t, type) {
  switch (type) {
    case "createRide":
      return t("outbox.opType.createRide");
    case "createScoreSheet":
      return t("outbox.opType.createScoreSheet");
    case "updateScoreSheet":
      return t("outbox.opType.updateScoreSheet");
    case "completeScoreSheet":
      return t("outbox.opType.completeScoreSheet");
    case "confirmScoreSheetMarks":
      return t("outbox.opType.confirmScoreSheetMarks");
    case "panelSignScoreSheet":
      return t("outbox.opType.panelSignScoreSheet");
    case "raiseUtilityEvent":
      return t("outbox.opType.raiseUtilityEvent");
    case "clearUtilityEvent":
      return t("outbox.opType.clearUtilityEvent");
    default:
      return type;
  }
}

// Visible badge for the local-first outbox (#420/#423): shows the count of
// unsynced items and clears when the queue drains. Offline it reads
// "N pending"; a wedged queue reads "retrying".
//
// #894 — a queue item that's wedged in the engine's error/backoff retry
// (sync.js's errorCount, previously discarded by ScoreContext) is NOT actively
// draining: it silently retries on its own backoff schedule (up to 60s) and,
// if it's a genuinely non-transient failure, may never succeed. Reporting
// that state as plain "Syncing N…" reads as a stuck spinner regardless of
// connection — the badge must instead say "retrying", so it settles to an
// accurate state (rather than clearing only-when-it-happens-to-succeed) and
// stays legible as long as the queue holds errored ops.
//
// #1283 — there is deliberately NO healthy-online state. Scribe autosave
// queues an outbox op on every keystroke, so an online "Syncing N…" chip
// mounted and unmounted a few hundred ms later, over and over, while a scribe
// typed. As an inline flex child of the session header it reflowed the whole
// row on each mount — at a narrowed window the row wraps and the entire line
// visibly jumps. A successful background push is also not information the
// scribe needs: the OfflineIndicator beside this already says "Online". So the
// badge earns its space only when work is actually held (offline) or actually
// failing (errorSync) — never merely in flight.
//
// #1921 — a permanent (non-401 4xx) op now reaches a TERMINAL state in
// sync.js instead of retrying forever: it must not keep saying "retrying…"
// (that stopped being true). The chip is also now tappable whenever
// something is surfaced, opening a detail dialog that names every stuck op
// (ride, judge position, show, attempt count, errorClass, raw lastError),
// explains a known status in plain words, offers a per-op Discard whose
// confirm names the ride, and lets the whole outbox be copied to the
// clipboard for a ringside diagnosis with no devtools. Nothing here ever
// discards an op on its own — discardOp only ever runs off the confirm
// button below.
export default function SyncStatus() {
  const { pendingSync, errorSync, terminalSync, heldSync, blockedSync, online } =
    useScoreContext();
  const { t } = useTranslation("score");
  const auth = useAuth();
  const [detailOpen, setDetailOpen] = useState(false);
  const [ops, setOps] = useState([]);
  // #1921 AC7 — size of the FULL outbox (pending + error + terminal), kept
  // separate from `ops` (which only ever holds SURFACED/stuck rows, the
  // AC4 list). Download must stay enabled off this count, not ops.length,
  // or a queue that hasn't surfaced anything yet reads as nothing-to-export.
  const [outboxCount, setOutboxCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [discardTarget, setDiscardTarget] = useState(null);
  // #2111 — which op is mid-retry, so its row shows a spinner and can't be
  // double-tapped; null when nothing is retrying.
  const [retryingOpId, setRetryingOpId] = useState(null);
  // #2031 — which op is mid-claim, so its row shows a spinner and can't be
  // double-tapped; null when nothing is being claimed. Mirrors retryingOpId.
  const [claimingOpId, setClaimingOpId] = useState(null);
  // #2111 — a kiosk reaching for this download is often ALREADY in a bad
  // IndexedDB state (blocked/closing connection, the same class of failure
  // that can leave sync stuck in the first place), so dumpDatabase() failing
  // outright is a real case, not a hypothetical. Surfaced in words rather
  // than a silent no-op button.
  const [downloadError, setDownloadError] = useState(false);

  const refreshDetail = useCallback(async () => {
    setLoading(true);
    try {
      const [surfaced, all] = await Promise.all([
        syncEngine.listSurfacedOps(),
        syncEngine.listAllOps(),
      ]);
      setOps(await describeOutboxOps(surfaced));
      setOutboxCount(all.length);
    } finally {
      setLoading(false);
    }
  }, []);

  const openDetail = useCallback(() => {
    setDetailOpen(true);
    void refreshDetail();
  }, [refreshDetail]);

  const closeDetail = useCallback(() => {
    setDetailOpen(false);
    setDiscardTarget(null);
  }, []);

  const confirmDiscard = useCallback(async () => {
    if (!discardTarget) return;
    await syncEngine.discardOp(discardTarget.opId);
    setDiscardTarget(null);
    await refreshDetail();
  }, [discardTarget, refreshDetail]);

  // #2111 — replaces the old clipboard-only "Copy outbox" export (AC1/AC2):
  // a real file download of the WHOLE local database — every store in
  // db.js's STORES, not just the outbox — via dumpDatabase(). A scribe
  // diagnosing a stuck kiosk with no devtools hands over one file that
  // covers a bad/never-hydrated class or ride cache just as well as a bad
  // outbox row, not only the outbox. Filename per the card spec: fv_scribe_
  // <email snake cased>_<timestamp>.json. Read-only, dumped fresh at
  // download time so it never goes stale against ops enqueued after the
  // dialog opened.
  const downloadLocalData = useCallback(async () => {
    try {
      const dump = await dumpDatabase();
      const filename = localDataExportFilename(auth.user?.profile?.email);
      triggerJsonDownload(filename, dump);
      setDownloadError(false);
    } catch {
      // #2111 — dumpDatabase()/Blob/anchor can all reject/throw (a blocked
      // or closing IndexedDB connection, a browser that refuses the
      // download). Never swallow it: this button is the recovery affordance
      // itself, so a silent failure here is the worst possible place for one.
      setDownloadError(true);
    }
  }, [auth.user]);

  // #2111 AC4 — human-initiated retry of exactly one surfaced op, wired to
  // syncEngine.retryOp. Held rows never get here: the button itself is
  // gated on !isHeldForDisplay(op) below (a held op is blocked on
  // ownership, not a failure a retry can fix — see retryOp's own doc
  // comment in sync.js).
  const retryOp = useCallback(
    async (opId) => {
      setRetryingOpId(opId);
      try {
        await syncEngine.retryOp(opId);
        await refreshDetail();
      } finally {
        setRetryingOpId(null);
      }
    },
    [refreshDetail],
  );

  // #2031 AC1 — human-initiated claim of exactly one ownerless held op, wired
  // to syncEngine.claimOp. Offered only when isClaimable(op) gates the button
  // in OutboxOpRow below (AC2's hard boundary: never for an op owned by a
  // different, known sub).
  const claimOp = useCallback(
    async (opId) => {
      setClaimingOpId(opId);
      try {
        await syncEngine.claimOp(opId);
        await refreshDetail();
      } finally {
        setClaimingOpId(null);
      }
    },
    [refreshDetail],
  );

  if (!pendingSync) return null;

  // #1921 — as soon as ANY stuck op is terminal, "retrying…" is no longer
  // true for the badge as a whole (some of what it's counting will never
  // retry again), so the wording drops it. A queue that's ENTIRELY
  // transient-and-still-retrying keeps the exact current wording.
  const stuck = terminalSync > 0;

  // #1921: the detail dialog is reachable from the PENDING chip too (that's
  // what makes the AC7 export reachable before anything has surfaced), and in
  // that state the stuck-items wording is simply false: a scribe with three
  // queued writes was being told "Nothing is stuck right now." under a title
  // reading "Stuck sync items", and then handed a Copy outbox button that
  // exports all three. Say what is actually true instead: the queue is
  // non-empty, none of it has gone wrong, and keep "Nothing is stuck right
  // now." for the genuinely-empty outbox it was written for.
  const queuedNoneStuck = !loading && ops.length === 0 && outboxCount > 0;

  let chip;
  if (errorSync > 0) {
    chip = (
      <Chip
        data-testid="sync-status"
        data-sync-state={stuck ? "terminal" : "error"}
        size="small"
        icon={<SyncProblemIcon />}
        label={
          stuck
            ? t("status.syncIssueStuck", { count: errorSync })
            : t("status.syncIssue", { count: errorSync })
        }
        color="error"
        variant="outlined"
        clickable
        onClick={openDetail}
      />
    );
  } else if (heldSync > 0) {
    // #1923 — a held op is neither errorSync (never attempted, status stays
    // "pending", see sync.js's isSurfacedError) nor gated on `!online` (it
    // is held regardless of connectivity — the whole point is it must NOT
    // go out under this session's token). Without this branch a scribe who
    // signed in online, right after a user swap, saw NOTHING: the error
    // branch above didn't fire and the online-healthy branch below returns
    // null, so the exact WEC 2026-08-23 case stayed invisible. color=
    // "warning" (not "error"): these ops didn't fail, they were correctly
    // withheld.
    chip = (
      <Chip
        data-testid="sync-status"
        data-sync-state="held"
        size="small"
        icon={<SyncProblemIcon />}
        label={t("status.syncHeld", { count: heldSync })}
        color="warning"
        variant="outlined"
        clickable
        onClick={openDetail}
      />
    );
  } else if (blockedSync > 0) {
    // #2031 AC3 — a blocked op is, like a held one, neither errorSync (never
    // attempted) nor gated on `!online` (it's withheld by the #882 ordering
    // gate regardless of connectivity — its own createRide op is the thing
    // that's stuck, not the network). Without this branch it fell through to
    // the online-healthy branch below and returned null: invisible, exactly
    // the gap the card names as worse than the visible ownership-held case.
    chip = (
      <Chip
        data-testid="sync-status"
        data-sync-state="blocked"
        size="small"
        icon={<SyncProblemIcon />}
        label={t("status.syncBlocked", { count: blockedSync })}
        color="warning"
        variant="outlined"
        clickable
        onClick={openDetail}
      />
    );
  } else if (!online) {
    // #1921 AC7 — nothing has surfaced as stuck yet (still pending, or an
    // error below SURFACE_AFTER_ATTEMPTS), but the outbox export still has
    // to be reachable here: whether a queued write is fine or about to
    // become the next terminal op isn't visible from this badge alone, and
    // that shouldn't gate whether its payload can be seen at all. This chip
    // used to render with no onClick/Dialog, so the export was completely
    // unreachable for exactly the queue state the export exists to explain.
    chip = (
      <Chip
        data-testid="sync-status"
        data-sync-state="pending"
        size="small"
        icon={<SyncIcon />}
        label={t("status.pendingSync", { count: pendingSync })}
        color="warning"
        variant="outlined"
        clickable
        onClick={openDetail}
      />
    );
  } else {
    // Healthy and online: the queue is draining on its own, say nothing (#1283).
    return null;
  }

  return (
    <>
      {chip}
      <Dialog
        open={detailOpen}
        onClose={closeDetail}
        maxWidth="sm"
        fullWidth
        data-testid="sync-detail-dialog"
      >
        <DialogTitle>
          {queuedNoneStuck
            ? t("outbox.detailTitleQueued")
            : t("outbox.detailTitle")}
        </DialogTitle>
        <DialogContent>
          {/* #1923 AC4/AC3 — surfaced once, plainly, at the top of the same
              detail view #1921 built, rather than a separate place to look:
              "N queued items belong to a previous session and were not
              sent." Each held row below still gets its own explanation
              (OutboxOpRow), this banner is the one-line summary. */}
          {!loading && heldSync > 0 && (
            <Typography
              variant="body2"
              color="text.primary"
              data-testid="sync-detail-held-banner"
              sx={{ mb: 1 }}
            >
              {t("outbox.heldBanner", { count: heldSync })}
            </Typography>
          )}
          {/* #2031 AC3 — same one-line-summary pattern as the held banner
              above, but with accurate wording: a blocked op was NOT queued by
              someone else, it's withheld by its own createRide op still
              being stuck (#882's ordering gate), so "belongs to a previous
              session" would be a false claim about it. */}
          {!loading && blockedSync > 0 && (
            <Typography
              variant="body2"
              color="text.primary"
              data-testid="sync-detail-blocked-banner"
              sx={{ mb: 1 }}
            >
              {t("outbox.blockedBanner", { count: blockedSync })}
            </Typography>
          )}
          {loading ? (
            <Typography>{t("outbox.loading")}</Typography>
          ) : queuedNoneStuck ? (
            <Typography data-testid="sync-detail-queued">
              {t("outbox.queuedNoneStuck", { count: outboxCount })}
            </Typography>
          ) : ops.length === 0 ? (
            <Typography>{t("outbox.empty")}</Typography>
          ) : (
            <List data-testid="sync-detail-list" disablePadding>
              {ops.map((op) => (
                <OutboxOpRow
                  key={op.opId}
                  op={op}
                  t={t}
                  onDiscard={() => setDiscardTarget(op)}
                  onRetry={() => retryOp(op.opId)}
                  retrying={retryingOpId === op.opId}
                  onClaim={() => claimOp(op.opId)}
                  claiming={claimingOpId === op.opId}
                />
              ))}
            </List>
          )}
        </DialogContent>
        <DialogActions sx={{ flexWrap: "wrap" }}>
          {/* #2111 — downloadLocalData's catch branch above; visible rather
              than a silent no-op on a button that IS the recovery path. */}
          {downloadError && (
            <Typography
              variant="body2"
              color="error"
              data-testid="sync-detail-download-error"
              sx={{ width: "100%", mb: 0.5 }}
            >
              {t("outbox.downloadFailed")}
            </Typography>
          )}
          {/* #1921 (screenshot-caught, dark mode) — a plain text Button
              defaults to MUI's primary color, which is too close to the
              dark Paper background to read (same "primary grey invisible
              in dark" issue as elsewhere in this app). color="inherit"
              mirrors RideList.jsx's own dialog Cancel buttons. */}
          {/* #2111 — unlike the old Copy Outbox (gated on outboxCount so it
              couldn't export nothing), this now dumps the WHOLE local
              database, not just the outbox, and this dialog can only ever
              be open while pendingSync > 0 (see the early `!pendingSync`
              return above) — so there is always something to hand over. */}
          <Button
            color="inherit"
            startIcon={<DownloadIcon />}
            onClick={downloadLocalData}
            data-testid="sync-detail-download"
          >
            {t("outbox.download")}
          </Button>
          <Button
            color="inherit"
            onClick={closeDetail}
            data-testid="sync-detail-close"
          >
            {t("outbox.close")}
          </Button>
        </DialogActions>
      </Dialog>
      <Dialog
        open={Boolean(discardTarget)}
        onClose={() => setDiscardTarget(null)}
        data-testid="sync-discard-confirm"
      >
        <DialogTitle>{t("outbox.discardTitle")}</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ mb: 2 }}>
            {discardTarget
              ? t("outbox.discardBody", {
                  ride: describeRideForDiscard(discardTarget),
                  op: opTypeLabel(t, discardTarget.type),
                })
              : null}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          {/* #1921 (screenshot-caught, dark mode) — same fix as above. */}
          <Button color="inherit" onClick={() => setDiscardTarget(null)}>
            {t("outbox.discardCancel")}
          </Button>
          <Button
            color="error"
            variant="contained"
            data-testid="sync-discard-confirm-button"
            onClick={confirmDiscard}
          >
            {t("outbox.discardConfirm")}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}

// #1921 — one row in the detail dialog: everything the AC asks for (op
// type, ride, judge position, show, attempt count, errorClass, raw
// lastError), a plain-words explanation when the status is a known one
// (currently just #1562's 403), and the per-op Discard action.
// #2111 AC3/AC4/AC5 — plus the class + ride time (so a scribe can tell
// which ride on the sheet this is without leaving the dialog), a per-op
// Retry button (non-held rows only — see retryOp's doc comment in
// sync.js), and a closed-by-default error-detail expander.
// #2031 AC1/AC2/AC3 — plus a per-op Claim button (held-and-ownerless rows
// only, see isClaimable), and a distinct explanation/no-Retry treatment for
// a row that's BLOCKED (#882 ordering gate) rather than held (ownership).
function OutboxOpRow({ op, t, onDiscard, onRetry, retrying, onClaim, claiming }) {
  const notAssignedScribe = isNotAssignedScribeError(op);
  // #1923 / #2031 — now reads real ownership (isHeldForDisplay is
  // owner-aware, see its own comment in outboxDetail.js) rather than
  // guessing from op.status.
  const held = isHeldForDisplay(op);
  // #2031 AC3 — a distinct cause from ownership: withheld by the #882
  // ordering gate because its own createRide op is itself stuck. Only
  // meaningful when the row isn't ALSO ownership-held — "held wins" is the
  // same one-op-one-state-one-wording rule sync.js's heldCount already
  // applies (#1923 F4).
  const blocked = !held && op.blockedOnStuckRide;
  // #2031 AC2 — the hard boundary: Claim is offered ONLY for a held row with
  // NO owner at all, never for one owned by a different, known sub.
  const claimable = held && isClaimable(op);
  const classLabel = classNumberLabel(op);
  const rideTime = formatVenueTime(op.rideScheduledTime);
  return (
    <>
      <ListItem
        data-testid="sync-detail-op"
        data-op-status={op.status}
        data-op-held={held}
        data-op-blocked={blocked}
        data-op-claimable={claimable}
        alignItems="flex-start"
        secondaryAction={
          <Stack direction="row" spacing={0.5}>
            {/* #2031 AC1/AC2 — offered only for a claimable (held, no
                owner) row; never for a row owned by a different, known sub
                (that row is still `held`, but isClaimable reads false for
                it), and never for a blocked row (claiming ownership does
                nothing for an ordering-gate block). */}
            {claimable && (
              <IconButton
                aria-label={t("outbox.claimAria")}
                data-testid="sync-claim-op"
                onClick={onClaim}
                disabled={claiming}
              >
                {claiming ? (
                  <CircularProgress size={20} />
                ) : (
                  <AssignmentIndIcon />
                )}
              </IconButton>
            )}
            {/* #2111 AC4 — judge bug finding: a held op is blocked on
                ownership, not a failure, so Retry never renders for one —
                sync.js's retryOp guards the same case server-side, this is
                the belt half of belt-and-braces. #2031 — a blocked row is
                the same story for a different reason (an ordering gate, not
                a failure): nothing retryOp does touches this op itself. */}
            {!held && !blocked && (
              <IconButton
                aria-label={t("outbox.retryAria")}
                data-testid="sync-retry-op"
                onClick={onRetry}
                disabled={retrying}
              >
                {retrying ? (
                  <CircularProgress size={20} />
                ) : (
                  <ReplayIcon />
                )}
              </IconButton>
            )}
            <IconButton
              edge="end"
              aria-label={t("outbox.discardAria")}
              data-testid="sync-discard-op"
              onClick={onDiscard}
            >
              <DeleteOutlineIcon />
            </IconButton>
          </Stack>
        }
      >
        <ListItemText
          secondaryTypographyProps={{ component: "div" }}
          primary={
            <Stack
              direction="row"
              spacing={1}
              alignItems="baseline"
              flexWrap="wrap"
            >
              <Typography variant="subtitle2" component="span">
                {opTypeLabel(t, op.type)}
              </Typography>
              {op.showName && (
                <Typography
                  variant="body2"
                  color="text.secondary"
                  component="span"
                >
                  {op.showName}
                </Typography>
              )}
            </Stack>
          }
          secondary={
            <Stack spacing={0.5} sx={{ mt: 0.5 }} component="span">
              <Typography variant="body2" component="span">
                {describeRideForDiscard(op)}
                {op.riderName ? ` — ${op.riderName}` : ""}
              </Typography>
              {/* #2111 AC3 — class + ride time, alongside show/entry/judge
                  position, shown regardless of held/error/terminal status
                  (same treatment as showName above): a scribe naming which
                  ride this is doesn't depend on whether it ever synced. */}
              {classLabel && (
                <Typography
                  variant="body2"
                  color="text.secondary"
                  component="span"
                  data-testid="sync-detail-op-class"
                >
                  {t("outbox.classLabel", { classLabel })}
                </Typography>
              )}
              {rideTime && (
                <Typography
                  variant="body2"
                  color="text.secondary"
                  component="span"
                  data-testid="sync-detail-op-time"
                >
                  {t("outbox.rideTime", { time: rideTime })}
                </Typography>
              )}
              {op.judgePosition && (
                <Typography
                  variant="body2"
                  color="text.secondary"
                  component="span"
                >
                  {t("outbox.judgePosition", { position: op.judgePosition })}
                </Typography>
              )}
              {/* #1923 / #2031 — a blocked op has never been attempted
                  (flush's #882 ordering gate skips it before it ever tries),
                  so its attempts/errorClass would read as "0 attempts" / an
                  empty error class: noise, not signal. A held op is NOT the
                  same guarantee — one attempted under its real owner before
                  that owner got swapped out (gap-3, isHeldForDisplay's own
                  comment) can carry real attempts/errorClass from that
                  earlier try. Either way the held/blocked explanations below
                  say the true, relevant thing instead, so both are hidden
                  here regardless. */}
              {!held && !blocked && (
                <>
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    component="span"
                  >
                    {t("outbox.attempts", { count: op.attempts })}
                  </Typography>
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    component="span"
                  >
                    {t("outbox.errorClass", { errorClass: op.errorClass })}
                  </Typography>
                </>
              )}
              {notAssignedScribe && (
                <Typography
                  variant="body2"
                  color="text.primary"
                  component="span"
                >
                  {t("outbox.explain403")}
                </Typography>
              )}
              {/* #2031 AC1/AC2 — two distinct wordings for two distinct
                  causes: a claimable (no-owner) row must invite the Claim
                  action, a foreign-known-owner row must NOT (it isn't
                  claimable, and offering the same "you can act on this"
                  framing would contradict the Claim button's own absence). */}
              {held && claimable && (
                <Typography
                  variant="body2"
                  color="text.primary"
                  component="span"
                  data-testid="sync-detail-op-held-explain"
                >
                  {t("outbox.explainHeldClaimable")}
                </Typography>
              )}
              {held && !claimable && (
                <Typography
                  variant="body2"
                  color="text.primary"
                  component="span"
                  data-testid="sync-detail-op-held-explain"
                >
                  {t("outbox.explainHeldForeign")}
                </Typography>
              )}
              {/* #2031 AC3 — a blocked row's own status stays "pending"
                  forever without this: it never displays as held (wrong
                  cause) and, before this card, never appeared in this list
                  at all. */}
              {blocked && (
                <Typography
                  variant="body2"
                  color="text.primary"
                  component="span"
                  data-testid="sync-detail-op-blocked-explain"
                >
                  {t("outbox.explainBlocked")}
                </Typography>
              )}
              <Typography
                variant="caption"
                color="text.secondary"
                component="span"
              >
                {op.lastError ?? t("outbox.noError")}
              </Typography>
              {/* #2111 AC5 — closed-by-default expander with the RAW API
                  detail. Held/blocked rows never attempted, so there's
                  nothing to show — same gate as attempts/errorClass above.
                  Defaults collapsed (no `expanded`/`defaultExpanded` prop). */}
              {!held && !blocked && (
                <Accordion
                  disableGutters
                  elevation={0}
                  sx={{
                    bgcolor: "transparent",
                    "&:before": { display: "none" },
                  }}
                  data-testid="sync-detail-op-error-expander"
                >
                  <AccordionSummary
                    expandIcon={<ExpandMoreIcon fontSize="small" />}
                    sx={{ px: 0, minHeight: 0, "& .MuiAccordionSummary-content": { my: 0.5 } }}
                  >
                    <Typography variant="caption" color="text.secondary">
                      {t("outbox.errorDetailToggle")}
                    </Typography>
                  </AccordionSummary>
                  <AccordionDetails sx={{ px: 0, pt: 0 }}>
                    <Stack spacing={0.5}>
                      <Typography variant="caption">
                        {t("outbox.httpStatus", {
                          status: op.httpStatus ?? t("outbox.unknownValue"),
                        })}
                      </Typography>
                      <Typography variant="caption">
                        {t("outbox.errorClassDetail", {
                          errorClass: op.errorClass ?? t("outbox.unknownValue"),
                        })}
                      </Typography>
                      <Typography
                        variant="caption"
                        sx={{ whiteSpace: "pre-wrap", wordBreak: "break-word" }}
                        data-testid="sync-detail-op-raw-error"
                      >
                        {/* #2111 — MUST fall back to lastError: after this
                            change lastError becomes the generic "Server
                            Error" for any API rejection, and a pre-#2111
                            row has rawErrorDetail: null, so without this
                            fallback the expander renders empty for it. */}
                        {op.rawErrorDetail ?? op.lastError ?? t("outbox.noError")}
                      </Typography>
                    </Stack>
                  </AccordionDetails>
                </Accordion>
              )}
            </Stack>
          }
        />
      </ListItem>
      <Divider component="li" />
    </>
  );
}
